// Alternates in an add-on (Spencer, 2026-10-17): an add-on that makes an item another catalog item does it with an
// alternate, its own row on the job (L-02A for L-02) priced only with the add-on on, never by swapping the base row. It
// takes the sizes and the inputs both items share, but not a price typed for the other item (the drawer base has its own
// Cabinotch quote); a child the replaced item makes that it does not (an applied end panel) waits for Keep or Leave out.
// The carry rule and the list of children are the engine's (packages/engine/src/alternates.ts).

import { carryToAlternate, type AlternateGap, type Library, type Scalar } from '@zerogap/engine';
import { insertRow, updateRow, type Row } from './api';

/** The next free number for an alternate of `number`: L-02A, then L-02B. */
export function alternateNumber(number: string, taken: Set<string>): string {
  for (const letter of 'ABCDEFGHJKLMNPQRSTUVWXYZ') {
    const n = `${number}${letter}`;
    if (!taken.has(n.toLowerCase())) return n;
  }
  return `${number}-alt`;
}

/** What the alternate of `target` as `catalogId` starts with: inputs and held values both items share, never another item's typed price. */
export function alternateStart(lib: Library, target: Row, catalogId: string): { inputs: Record<string, Scalar>; overrides: Record<string, Scalar>; blank: string[] } {
  const from = target.catalog_item_id ? lib.catalog[target.catalog_item_id]?.inputs ?? null : null;
  const to = lib.catalog[catalogId]?.inputs ?? [];
  const same = target.catalog_item_id === catalogId;
  const inputs = carryToAlternate(from, to, (target.inputs ?? {}) as Record<string, Scalar>, same);
  const overrides = carryToAlternate(from, to, (target.overrides ?? {}) as Record<string, Scalar>, same);
  return { inputs: inputs.carried, overrides: overrides.carried, blank: [...new Set([...inputs.blank, ...overrides.blank])] };
}

/**
 * An alternate for each target, as `catalogId`, in the add-on: the shared inputs, then what was typed for the new item.
 * Returns the numbers made, each with the typed prices it starts without.
 */
export async function addAlternates(o: { jobId: string; lib: Library; addOnId: string; targets: Row[]; catalogId: string; typed: Record<string, Scalar>; jobItems: Row[] }): Promise<{ number: string; blank: string[] }[]> {
  const taken = new Set(o.jobItems.map((i) => String(i.number ?? '').toLowerCase()));
  const made: { number: string; blank: string[] }[] = [];
  for (const t of o.targets) {
    const start = alternateStart(o.lib, t, o.catalogId);
    const number = alternateNumber(String(t.number), taken);
    taken.add(number.toLowerCase());
    // only what the new item has: a value typed for an input it lacks is not carried
    const keys = new Set((o.lib.catalog[o.catalogId]?.inputs ?? []).map((i) => i.key));
    const typed = Object.fromEntries(Object.entries(o.typed).filter(([k, v]) => keys.has(k) && v !== null && v !== undefined && String(v).trim() !== ''));
    await insertRow('job_item', {
      job_id: o.jobId,
      number,
      name: t.name,
      catalog_item_id: o.catalogId,
      inputs: { ...start.inputs, ...typed },
      overrides: start.overrides,
      room_id: t.room_id ?? null,
      sort: t.sort ?? 0,
      add_on_id: o.addOnId,
      replaces_item_id: t.id,
    });
    made.push({ number, blank: start.blank.filter((k) => !(k in typed)) });
  }
  return made;
}

/** Keep a child the replaced item made: its own row under the alternate, built to what it was (its typed and handed-down values). */
export async function keepChild(jobId: string, gap: AlternateGap, number: string, sort: number) {
  await insertRow('job_item', {
    job_id: jobId,
    number,
    name: gap.child.name,
    catalog_item_id: gap.child.catalog_item_id,
    parent_item_id: gap.alternate_id,
    child_index: null,
    inputs: gap.child.inputs,
    sort,
  });
}

/** Leave a child out on purpose: recorded on the alternate, so it is not asked again. */
export async function leaveChildOut(alternate: Row, gap: AlternateGap) {
  const left = Array.isArray(alternate.children_left_out) ? (alternate.children_left_out as string[]) : [];
  await updateRow('job_item', alternate.id, { children_left_out: [...left, gap.child.catalog_item_id ?? `custom:${gap.child.id}`] });
}

/**
 * An add-on made before 2026-10-17 that swaps its items to another catalog item becomes alternates: one per item it touches,
 * started by the carry rule with the swap's typed inputs over them; the add-on then touches nothing on the base rows.
 */
export async function swapToAlternates(o: { jobId: string; lib: Library; addOn: Row; jobItems: Row[] }): Promise<{ number: string; blank: string[] }[]> {
  const swap = (o.addOn.recipe_swap ?? {}) as { catalog_item_id?: string; inputs?: Record<string, Scalar>; overrides?: Record<string, Scalar> };
  if (!swap.catalog_item_id) return [];
  const targets = ((o.addOn.target_items ?? []) as string[]).map((id) => o.jobItems.find((i) => i.id === id)).filter((x): x is Row => !!x && !x.parent_item_id);
  const made = await addAlternates({ jobId: o.jobId, lib: o.lib, addOnId: o.addOn.id, targets, catalogId: swap.catalog_item_id, typed: { ...(swap.inputs ?? {}), ...(swap.overrides ?? {}) }, jobItems: o.jobItems });
  await updateRow('add_on', o.addOn.id, { target_items: [], recipe_swap: {} });
  return made;
}
